import {ReactNode, useCallback, useEffect, useMemo, useRef, useState} from "react";

export interface AutocompleteMessages {
	loading?: string;
	/** "{0}" is replaced with the minimum character count. */
	minChars?: string;
	noResults?: string;
	error?: string;
}

export interface AutocompleteFindOptions<TItem> {
	fetchItems: (query: string, signal: AbortSignal) => Promise<TItem[]>;
	/** true = fetch per user input (RemotePartial); false = fetch the full list once on open (RemoteFull). */
	find: boolean;
	minChars?: number;
	debounceMs?: number;
	messages?: AutocompleteMessages;
}

export interface AutocompleteFindResult<TItem> {
	items: TItem[];
	pickerMessage: ReactNode;
	/** Only set in find mode; wiring it into SelectBox switches it to controlled-find. */
	onFindChange?: (query: string) => void;
	/** Only set in full mode; SelectBox may call it repeatedly while open. */
	onPickerOpen?: () => void;
}

type FindStatus = "idle" | "belowMin" | "loading" | "done" | "empty" | "error";

/**
 * The fetch pipeline shared by AutocompleteBox and MvvmAutocompleteBox: trailing debounce,
 * min-chars gate, abort-based race protection and a deferred (150ms) loading message so fast
 * responses never flash a spinner.
 */
export function useAutocompleteFind<TItem>(options: AutocompleteFindOptions<TItem>): AutocompleteFindResult<TItem> {
	const {fetchItems, find, minChars = 3, debounceMs = 300, messages} = options;

	const [items, setItems] = useState<TItem[]>([]);
	const [status, setStatus] = useState<FindStatus>(find ? "belowMin" : "idle");
	const [showLoading, setShowLoading] = useState(false);
	const debounceRef = useRef<number>(undefined);
	const spinnerRef = useRef<number>(undefined);
	const abortRef = useRef<AbortController | null>(null);
	const fetchedRef = useRef(false);

	useEffect(() => () => {
		window.clearTimeout(debounceRef.current);
		window.clearTimeout(spinnerRef.current);
		abortRef.current?.abort();
	}, []);

	const runFetch = useCallback(function (query: string): void {
		abortRef.current?.abort();
		const controller = new AbortController();
		abortRef.current = controller;
		setStatus("loading");
		setShowLoading(false);
		window.clearTimeout(spinnerRef.current);
		spinnerRef.current = window.setTimeout(() => setShowLoading(true), 150);

		fetchItems(query, controller.signal)
			.then((result) => {
				if (controller.signal.aborted) {
					return;
				}

				setItems(result);
				setStatus(result.length === 0 ? "empty" : "done");
			})
			.catch(() => {
				if (controller.signal.aborted) {
					return;
				}

				fetchedRef.current = false;
				setStatus("error");
			})
			.finally(() => {
				if (!controller.signal.aborted) {
					window.clearTimeout(spinnerRef.current);
				}
			});
	}, [fetchItems]);

	const onFindChange = useCallback(function (query: string): void {
		window.clearTimeout(debounceRef.current);
		const trimmed = query.trim();

		if (trimmed.length < minChars) {
			abortRef.current?.abort();
			window.clearTimeout(spinnerRef.current);
			setItems([]);
			setStatus("belowMin");
			return;
		}

		debounceRef.current = window.setTimeout(() => runFetch(trimmed), debounceMs);
	}, [minChars, debounceMs, runFetch]);

	const onPickerOpen = useCallback(function (): void {
		if (fetchedRef.current) {
			return;
		}

		fetchedRef.current = true;
		runFetch("");
	}, [runFetch]);

	const pickerMessage = useMemo((): ReactNode => {
		switch (status) {
			case "belowMin":
				return (messages?.minChars ?? (minChars === 1 ? "Type at least {0} character to search" : "Type at least {0} characters to search")).replace("{0}", minChars.toString());
			case "loading":
				return showLoading ? messages?.loading ?? "Searching…" : null;
			case "empty":
				return messages?.noResults ?? "No results";
			case "error":
				return messages?.error ?? "The search failed. Try again.";
			default:
				return null;
		}
	}, [status, showLoading, messages, minChars]);

	return {
		items,
		pickerMessage,
		onFindChange: find ? onFindChange : undefined,
		onPickerOpen: find ? undefined : onPickerOpen
	};
}
